import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Tag,
  Divider,
  Card,
  Descriptions,
  message,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  IncomeTransaction,
  IncomeSource,
  SourceWallet,
  incomeService,
  formatCurrency,
} from '../../services/incomeService';
import styles from '../common/DrawerModalMobile.module.css';

interface TransactionDetailModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: IncomeTransaction | undefined;
  source: IncomeSource | undefined;
  wallet: SourceWallet | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onReloadWallets?: () => void;
}

export const TransactionDetailModalMobile: React.FC<TransactionDetailModalMobileProps> = ({
  isOpen,
  onClose,
  transaction,
  source,
  wallet,
  onEdit,
  onDelete,
  onReloadWallets,
}) => {
  const [loading, setLoading] = useState(false);

  if (!transaction) {
    return null;
  }

  const getTransactionIcon = (transactionType: 'income' | 'expense') => {
    if (transactionType === 'income') {
      return <ArrowUpOutlined />;
    } else if (transactionType === 'expense') {
      return <ArrowDownOutlined />;
    }
    return <SwapOutlined />;
  };

  const getTransactionTypeText = (transactionType: 'income' | 'expense') => {
    if (transactionType === 'income') {
      return 'Thu nhập';
    } else if (transactionType === 'expense') {
      return 'Chi tiêu';
    }
    return 'Chuyển khoản';
  };

  const getTransactionTypeColor = (transactionType: 'income' | 'expense') => {
    if (transactionType === 'income') {
      return 'success';
    } else if (transactionType === 'expense') {
      return 'error';
    }
    return 'processing';
  };

  const getAmountColor = (transactionType: 'income' | 'expense') => {
    if (transactionType === 'income') {
      return '#10B981';
    } else if (transactionType === 'expense') {
      return '#EF4444';
    }
    return '#3B82F6';
  };

  const getAmountPrefix = (transactionType: 'income' | 'expense') => {
    if (transactionType === 'income') {
      return '+';
    } else if (transactionType === 'expense') {
      return '-';
    }
    return '';
  };

  return (
    <Drawer
      title="Chi tiết giao dịch"
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="85vh"
      className={styles.drawer}
      bodyStyle={{ 
        padding: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          {/* Header với icon và số tiền */}
          <div style={{ 
            textAlign: 'center', 
            padding: '24px 16px',
            borderBottom: '1px solid #f0f0f0',
            marginBottom: '16px'
          }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%',
              backgroundColor: getAmountColor(transaction.transaction_type),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              color: 'white',
              fontSize: '24px'
            }}>
              {getTransactionIcon(transaction.transaction_type)}
            </div>
            
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: getAmountColor(transaction.transaction_type) }}>
              {getAmountPrefix(transaction.transaction_type)}{formatCurrency(transaction.amount)}
            </div>
            
            <Tag 
              color={getTransactionTypeColor(transaction.transaction_type)}
              style={{ marginTop: '8px', fontSize: '14px', padding: '4px 12px' }}
            >
              {getTransactionTypeText(transaction.transaction_type)}
            </Tag>
          </div>

          {/* Thông tin cơ bản */}
          <div style={{ padding: '0 16px' }}>
            <Card title="Thông tin giao dịch" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Cột tiêu đề */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontWeight: 'bold', color: '#666' }}>Ngày giao dịch:</div>
                  <div style={{ fontWeight: 'bold', color: '#666' }}>Nguồn thu nhập:</div>
                  <div style={{ fontWeight: 'bold', color: '#666' }}>Ví:</div>
                  {transaction.notes && (
                    <div style={{ fontWeight: 'bold', color: '#666' }}>Ghi chú:</div>
                  )}
                </div>
                
                {/* Cột giá trị */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>{dayjs(transaction.income_date).format('DD/MM/YYYY')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{source?.icon || '💰'}</span>
                    <span>{source?.name || 'Không xác định'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <WalletOutlined />
                    <span>{wallet?.name || 'Không xác định'}</span>
                  </div>
                  {transaction.notes && (
                    <div>{transaction.notes}</div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
            <Button 
              onClick={onEdit}
              icon={<EditOutlined />}
              className={`${styles.drawerFooterButton} ${styles.default}`}
            >
              Chỉnh sửa
            </Button>
            <Button 
              onClick={onDelete} 
              danger
              icon={<DeleteOutlined />}
              className={`${styles.drawerFooterButton} ${styles.danger}`}
            >
              Xóa
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
